import { useState, useEffect } from "react";
import { createContext, useContext } from "react";
import { publicAnonClient as anonClient } from "@/lib/publicAnonClient";
import { isPlatformHost, normalizeHostname } from "@/utils/isPlatformHost";

interface CustomDomainContextType {
  /** The resolved site slug for the current custom domain, or null if on a platform host */
  slug: string | null;
  /** The org_id associated with this custom domain site */
  orgId: string | null;
  /** Whether the custom domain check is still in progress */
  loading: boolean;
}

const CustomDomainContext = createContext<CustomDomainContextType>({
  slug: null,
  orgId: null,
  loading: true,
});

export const useCustomDomain = () => useContext(CustomDomainContext);

/**
 * Checks if the current hostname is a custom domain mapped to an org site.
 * Provides the resolved slug via context so routes can decide what to render.
 * On custom domains: "/" → public site, "/portal/*" → member portal, etc.
 */
const CustomDomainRouter = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<"checking" | "resolved">("checking");
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null);
  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(null);

  useEffect(() => {
    const hostname = normalizeHostname(window.location.hostname);

    if (isPlatformHost(hostname)) {
      setState("resolved");
      return;
    }

    (async () => {
      try {
        const variants = [hostname];
        if (hostname.startsWith("www.")) {
          variants.push(hostname.slice(4));
        } else {
          variants.push(`www.${hostname}`);
        }

        const { data } = await anonClient
          .from("website_config")
          .select("site_slug, custom_domain, org_id")
          .not("custom_domain", "is", null)
          .eq("site_enabled", true)
          .limit(200);

        const match = (data || []).find((site) => {
          const customDomain = normalizeHostname(site.custom_domain || "");
          return variants.includes(customDomain);
        });

        setResolvedSlug(match?.site_slug || null);
        setResolvedOrgId(match?.org_id || null);
        setState("resolved");
      } catch {
        setState("resolved");
      }
    })();
  }, []);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-muted border-t-foreground rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <CustomDomainContext.Provider value={{ slug: resolvedSlug, orgId: resolvedOrgId, loading: false }}>
      {children}
    </CustomDomainContext.Provider>
  );
};

export default CustomDomainRouter;
